'use client';

import { useState, useCallback } from 'react';
import Link from 'next/link';
import { aiModelsApi, RmbgResponse } from '@/lib/ai-models-api';

type RmbgMode = 'remove' | 'mask';

export default function RmbgPage() {
  const [image, setImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [mode, setMode] = useState<RmbgMode>('remove');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RmbgResponse | null>(null);

  const handleImageChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const base64 = await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    });

    setImage(base64);
    setImagePreview(URL.createObjectURL(file));
    setResult(null);
    setError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return;

    setLoading(true);
    setError(null);

    try {
      const response = mode === 'remove'
        ? await aiModelsApi.rmbgRemove(image)
        : await aiModelsApi.rmbgMask(image);
      setResult(response);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[var(--bg-primary)]">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[var(--border-subtle)]">
        <div className="max-w-7xl mx-auto px-6 h-12 flex items-center gap-4">
          <Link
            href="/ai-models"
            className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>
          <h1 className="text-lg font-semibold">RMBG - Background Removal</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Left: Parameters (2 cols) */}
            <div className="lg:col-span-2 space-y-6">
              <form onSubmit={handleSubmit} className="bg-[var(--bg-elevated)] rounded-xl p-6 space-y-5">
                <h2 className="text-lg font-semibold">Parameters</h2>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Image</label>
                  <div className="relative">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Input"
                          className="w-full h-48 object-contain bg-[var(--bg-primary)] rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                            setResult(null);
                          }}
                          className="absolute top-2 right-2 p-1 bg-black/60 hover:bg-red-500 rounded transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[var(--border-subtle)] rounded-lg cursor-pointer hover:border-[var(--accent)] transition-colors">
                        <svg className="w-10 h-10 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                        </svg>
                        <span className="mt-2 text-sm text-[var(--text-muted)]">Upload image</span>
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>

                {/* Mode */}
                <div>
                  <label className="block text-sm font-medium mb-2">Mode</label>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value as RmbgMode)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded-lg focus:border-[var(--accent)] focus:outline-none"
                  >
                    <option value="remove">Remove Background (Transparent)</option>
                    <option value="mask">Generate Mask (Black/White)</option>
                  </select>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!image || loading}
                  className="w-full py-3 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-black font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Remove Background'}
                </button>

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                    {error}
                  </div>
                )}
              </form>
            </div>

            {/* Right: Results (3 cols) */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-[var(--bg-elevated)] rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4">Results</h2>

                {!result && !loading && (
                  <div className="flex flex-col items-center justify-center py-48 text-center">
                    <svg className="w-16 h-16 text-[var(--text-muted)] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.848 8.25l1.536.887M7.848 8.25a3 3 0 11-5.196-3 3 3 0 015.196 3zm1.536.887a2.165 2.165 0 011.083 1.839c.005.351.054.695.14 1.024M9.384 9.137l2.077 1.199" />
                    </svg>
                    <p className="text-[var(--text-muted)]">Upload an image to remove background</p>
                  </div>
                )}

                {loading && (
                  <div className="flex flex-col items-center justify-center py-48">
                    <div className="w-12 h-12 border-3 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-[var(--text-muted)]">Processing...</p>
                  </div>
                )}

                {result && (
                  <div className="space-y-4">
                    {/* Result Image */}
                    <div className="relative">
                      <p className="text-sm text-[var(--text-muted)] mb-2">
                        {mode === 'remove' ? 'Background Removed' : 'Mask'}
                      </p>
                      <div className="relative rounded-lg overflow-hidden" style={{
                        backgroundImage: mode === 'remove'
                          ? 'linear-gradient(45deg, #333 25%, transparent 25%), linear-gradient(-45deg, #333 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #333 75%), linear-gradient(-45deg, transparent 75%, #333 75%)'
                          : 'none',
                        backgroundSize: '20px 20px',
                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                        backgroundColor: mode === 'remove' ? '#222' : 'var(--bg-primary)'
                      }}>
                        <img
                          src={result.image.startsWith('data:') ? result.image : `data:image/png;base64,${result.image}`}
                          alt="Result"
                          className="w-full"
                        />
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="p-4 bg-[var(--bg-primary)] rounded-lg">
                      <p className="text-xs text-[var(--text-muted)] uppercase">Inference Time</p>
                      <p className="text-lg font-semibold">{result.inference_time_ms.toFixed(0)}ms</p>
                    </div>

                    {/* Download */}
                    <a
                      href={result.image.startsWith('data:') ? result.image : `data:image/png;base64,${result.image}`}
                      download={mode === 'remove' ? 'background_removed.png' : 'mask.png'}
                      className="block w-full py-3 bg-[var(--bg-primary)] hover:bg-[var(--bg-hover)] text-center font-medium rounded-lg transition-colors border border-[var(--border-subtle)]"
                    >
                      Download {mode === 'remove' ? 'Image' : 'Mask'}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
